import axios from "axios";
import { useMutation } from "react-query";
import { queryClient } from "../../..";
import { UIContent } from "../../../UIContent";
import { useGenErrorMsgBanners } from "../../../utils/useGenErrorMsgBanners";
import { useBannerContext } from "oolib";
import { useGetQueryData } from "@/utils/react-query-hooks/general";

const getDeleteSuccessMsgAsPerSegment = (contentType, deletedContent) => {
  const tpl = queryClient.getQueryData(["tpl", contentType]);
  const segment = tpl?.general?.segment;
  const successMsg =
    segment === "collections"
      ? UIContent.reactQueryHooks_content.deleteTagSuccessfulMsg.replace('$deletedContentTitle', deletedContent.main.title)
      : UIContent.reactQueryHooks_content.deleteContentSuccessfulMsg;

  return successMsg;
};

export const useDeleteContent = () => {
  const { SET_ALERT_BANNER } = useBannerContext();
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  const platformConfigs = useGetQueryData('platformConfigs')

  return useMutation(
    ({
      contentType,
      id,
      suppressBanner,
    }) => {
      return axios.delete(
        `/api/content/deleteContent/${contentType}/${id}`
      );
    },

    {
      onSuccess: (res, { contentType, suppressBanner }) => {
        /**
         * we use id so that, if there are multiple 
         * deleted successful banners in quick succession (e.g themes manager)
         * then every next banner will replace the previous, instead of stacking
         * them up
         */
        if (!suppressBanner) {
        SET_ALERT_BANNER({
          id: "useDeleteContent_onSuccess",
          msg: getDeleteSuccessMsgAsPerSegment(contentType, res.data.deletedContent),
          color: "green",
          timeOut: 5000
        });
      }
        queryClient.invalidateQueries(["RelatedContent"]);

        
        // Get all queries from the cache
        const queryCache = queryClient.getQueryCache();
        const queries = queryCache.getAll();
        
        // Find and invalidate contentListing queries that match the type
        queries.forEach(query => {
          const queryKey = query.queryKey;
          // Check if this is a contentListing query
          if (queryKey && queryKey[0] === 'contentListing') {
            // Check if queryKey[1] exists and contains the contentType
            if (queryKey[1] && Array.isArray(queryKey[1]) && queryKey[1].includes(contentType)) {
              queryClient.invalidateQueries(queryKey);
            }
          }
        });
    
        /**
         * needed on SELF MANAGED because:
         * if the count of themes or subthemes goes to / increases from zero,
         * we hide/show some features        
         */
        if (platformConfigs.SELF_MANAGED_BASE_CONFIGS?.enable === true) {
          // Update the platformConfigs cache to decrement the count
          queryClient.setQueryData('platformConfigs', (oldData) => {
            if (!oldData) return oldData;
            
            const updatedData = { ...oldData };
            
            // Make sure the nested structure exists
            if (updatedData.annotationTagsCount) {
              const currentCounts = { ...updatedData.annotationTagsCount };
              
              // Decrement the appropriate count based on content type
              if (contentType === 'tags' && typeof currentCounts.tags === 'number' && currentCounts.tags > 0) {
                currentCounts.tags -= 1;
              } else if (contentType === 'subThemes' && typeof currentCounts.subThemes === 'number' && currentCounts.subThemes > 0) {
                currentCounts.subThemes -= 1;
              } else if (contentType === 'themes' && typeof currentCounts.themes === 'number' && currentCounts.themes > 0) {
                currentCounts.themes -= 1;
              }
              
              updatedData.annotationTagsCount = currentCounts;
            }

            return updatedData;
          });
        }
      },
      onError: (err) => genErrorMsgBanners({ err }),
    }
  );
};
